/**
 * Nymia · Marketing Attribution capture (client side).
 *
 * Non-blocking, no external deps. Runs once at app bootstrap and stores
 * traffic-origin metadata in the browser so it survives:
 *   • SPA navigation      → sessionStorage snapshot
 *   • Session end + return→ localStorage first_touch (immutable) +
 *                           localStorage last_touch (updated on every
 *                           new visit that carries UTMs)
 *
 * Design goals:
 *   • Zero backend calls until the user reaches an authenticated action
 *     (register / login) — no idle attribution requests.
 *   • Schema is versioned so future migrations (v2, v3) can be added
 *     without breaking older stored payloads.
 *   • Unknown UTM-style params are still captured (forward-compat).
 */

// Bumped when the payload shape changes in a backward-incompatible way.
export const ATTRIBUTION_VERSION = 1;

// Standard params we always look for. Anything else that starts with
// `utm_` is also captured under `extra_utm` so no future utm parameter
// is silently dropped.
const KNOWN_PARAMS = [
  // UTM (RFC-ish)
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  // Ads platform click IDs
  'fbclid',   // Meta / Facebook
  'gclid',    // Google Ads
  'gbraid',   // Google Ads iOS
  'wbraid',   // Google Ads web
  'ttclid',   // TikTok Ads
  'msclkid',  // Microsoft / Bing Ads
  'twclid',   // X (Twitter) Ads
  'li_fat_id',// LinkedIn Ads
];

const SS_KEY = 'nymia_attribution_session';  // sessionStorage — current-tab snapshot
const LS_FIRST = 'nymia_attribution_first_touch';
const LS_LAST = 'nymia_attribution_last_touch';

const nowIso = () => new Date().toISOString();

const safeParse = (raw) => {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch (_e) { return null; }
};

const safeGet = (storage, key) => {
  try { return safeParse(storage.getItem(key)); } catch (_e) { return null; }
};

const safeSet = (storage, key, value) => {
  try { storage.setItem(key, JSON.stringify(value)); } catch (_e) { /* quota / private mode */ }
};

/**
 * Read query params + document.referrer + landing URL and produce a
 * flat attribution payload. Returns null when the URL carries NO
 * attribution signal (empty query + no referrer that matters).
 * Always returns an object with `captured_at` and `version` when any
 * meaningful field is present.
 */
export const captureAttributionFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const params = url.searchParams;

  const payload = {
    version: ATTRIBUTION_VERSION,
    captured_at: nowIso(),
    landing_path: url.pathname + url.search,
    referrer: (document.referrer || '').slice(0, 512),
  };

  let hasSignal = false;
  const extra = {};
  for (const [key, value] of params.entries()) {
    if (!value) continue;
    const trimmed = value.slice(0, 256);
    if (KNOWN_PARAMS.includes(key)) {
      payload[key] = trimmed;
      hasSignal = true;
    } else if (key.startsWith('utm_')) {
      extra[key] = trimmed;
      hasSignal = true;
    }
  }
  if (Object.keys(extra).length) payload.extra_utm = extra;

  // Even without params, a non-empty external referrer is still a
  // useful attribution signal ("came from google.com/search").
  if (!hasSignal && !payload.referrer) return null;

  return payload;
};

/**
 * Read the current-tab attribution snapshot (survives SPA navigation).
 * Fallbacks to localStorage `last_touch` when the tab has no session
 * snapshot (e.g. deep-linked visit).
 */
export const getSessionAttribution = () => {
  if (typeof window === 'undefined') return null;
  return safeGet(sessionStorage, SS_KEY) || safeGet(localStorage, LS_LAST);
};

/** First-touch is IMMUTABLE once set. */
export const getFirstTouch = () => {
  if (typeof window === 'undefined') return null;
  return safeGet(localStorage, LS_FIRST);
};

/** Last-touch is the most recent visit that carried an attribution signal. */
export const getLastTouch = () => {
  if (typeof window === 'undefined') return null;
  return safeGet(localStorage, LS_LAST);
};

/**
 * Bootstrap called ONCE at app mount by <AttributionCapture />. Reads
 * the current URL, updates all three stores (session / first / last)
 * and returns whatever was captured.
 */
export const bootstrapAttribution = () => {
  if (typeof window === 'undefined') return null;
  const captured = captureAttributionFromUrl();
  if (captured) {
    // 1. sessionStorage: refresh the current-tab snapshot on every
    //    landing that carries a signal.
    safeSet(sessionStorage, SS_KEY, captured);

    // 2. localStorage first_touch: only if empty (immutable).
    if (!getFirstTouch()) safeSet(localStorage, LS_FIRST, captured);

    // 3. localStorage last_touch: always update.
    safeSet(localStorage, LS_LAST, captured);
  } else {
    // Even without URL params we still want a sessionStorage handle so
    // downstream code has something consistent to read.
    const cached = getSessionAttribution();
    if (cached) safeSet(sessionStorage, SS_KEY, cached);
  }
  return captured;
};

/**
 * Read the two Meta Pixel cookies so they can be sent to the server
 * side (Conversions API) for iOS 14.5+ deduplicated attribution.
 *   • _fbp: Facebook browser id     (persistent per subdomain)
 *   • _fbc: Facebook click id        (set by fbevents.js when fbclid
 *                                     is present in the URL)
 * When the pixel is not loaded (mock mode, cookie-consent refused)
 * these return null and CAPI just skips the field.
 */
const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(^|;\\s*)' + name + '=([^;]+)'));
  return m ? decodeURIComponent(m[2]) : null;
};

export const getMetaCookies = () => ({
  fbp: readCookie('_fbp'),
  fbc: readCookie('_fbc'),
});

/**
 * Assemble the full attribution envelope that gets POSTed to backend
 * routes (register, login). Callers pass this as `attribution` in
 * their request body. Backend accepts null / missing gracefully.
 */
export const buildAttributionEnvelope = () => {
  const first = getFirstTouch();
  const last = getLastTouch();
  const session = getSessionAttribution();
  if (!first && !last && !session) return null;
  const meta = getMetaCookies();
  return {
    first_touch: first || session || last || null,
    last_touch: last || session || null,
    meta_cookies: (meta.fbp || meta.fbc) ? meta : null,
  };
};
